import { Box, Button, Icon, Link, Text, useDisclosure } from '@ritualco/jutsu'
import { MdAdd } from 'react-icons/md'
import {
  useDeletePaymentMethod,
  useGetBillingQuery,
  usePaymentMethodSelect,
} from 'api/endpoints/paymentMethod'
import { EditableSection } from 'components/EditableSection'
import { PaymentMethodAddModal } from 'components/Modals/PaymentMethodAddModal'
import { MaxWidthPage } from 'components/PageTemplates'
import { PaymentMethodsTable } from 'components/Tables/PaymentMethodsTable'
import { useContent } from 'lib/content/react'
import { Link as ReactRouterLink } from 'lib/router'
import Loading from 'components/Loading'
import { useMessage } from 'contexts/MessageContext'
import { useAnalytics } from 'lib/simple-analytics'
import { AnalyticEvents } from 'types/entities'

export type BillingProps = {
  workspaceId: string
}

const Billing = ({ workspaceId }: BillingProps) => {
  const { formatMessage } = useContent()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { addMessage } = useMessage()
  const { track } = useAnalytics()
  const { status, data } = useGetBillingQuery({ workspaceId })
  const deletePaymentMethod = useDeletePaymentMethod()
  const selectPaymentMethod = usePaymentMethodSelect()

  if (status === 'error') {
    // TODO: handle error state
    return <span>Something went wrong!</span>
  }

  return (
    <MaxWidthPage
      heading={formatMessage({ id: 'pmportal.billing.title' })}
      subheading={
        <Box color="grayscale.secondary" fontSize="small">
          {formatMessage(
            { id: 'pmportal.billing.description' },
            {
              p: text => <Text _notFirst={{ paddingTop: 6 }}>{text}</Text>,
              help: text => (
                <Link
                  as={ReactRouterLink}
                  to={`/workspaces/${workspaceId}/settings/help`}
                >
                  {text}
                </Link>
              ),
            }
          )}
        </Box>
      }
    >
      <EditableSection
        title={formatMessage({ id: 'pmportal.billing.tableTitle' })}
        action={
          <Button
            colorScheme="transparent"
            variant="outline"
            leftIcon={<Icon as={MdAdd} w="20px" h="20px" />}
            onClick={onOpen}
          >
            {formatMessage({ id: 'pmportal.billing.actionAddCard' })}
          </Button>
        }
      >
        {status === 'loading' ? (
          <Loading />
        ) : (
          <PaymentMethodsTable
            paymentMethods={data}
            onRemove={paymentMethodId => {
              deletePaymentMethod.mutate(
                {
                  workspaceId,
                  paymentMethodId,
                },
                { onSuccess: () => track(AnalyticEvents.actionRemoveSuccess) }
              )
            }}
            onSwitch={paymentMethodId => {
              selectPaymentMethod.mutate(
                {
                  workspaceId,
                  paymentMethodId,
                },
                { onSuccess: () => track(AnalyticEvents.actionSwitchSuccess) }
              )
            }}
          />
        )}
      </EditableSection>

      <PaymentMethodAddModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={() => {
          addMessage({
            status: 'success',
            description: formatMessage({
              id: 'pmportal.billing.addCardSuccess',
            }),
            isClosable: false,
          })
        }}
        workspaceId={workspaceId}
      />
    </MaxWidthPage>
  )
}
export default Billing
